import {
    createContext,
    type FC,
    type ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDrag, useDrop } from "react-dnd";
import { LayerContext } from "@onshopfront/components";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import { useDragDropComponent } from "./DragDropManager";
import {
    DragDropEditorContext,
    type DragDropItem,
    type DragDropMoveItem,
    isDragDropNew,
} from "./DragDropTypes";

interface Props {
    id: string;
    index: number;
    selected: null | string;
    container: string;
}

export const DragDropComponentErrorContext = createContext<ReactNode>(null);
export const DragDropComponentEmptyContext = createContext<ReactNode>(null);

export const DragDropComponent: FC<Props> = ({
    id,
    index,
    selected,
    container,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const hasLayerRef = useRef(false);
    const belowRef = useRef<null | boolean>(null);
    const indexRef = useRef(index);
    const deletingRef = useRef(false);
    const deletingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {
        manager,
        accept,
        setSelected,
        onCreate,
        items,
        onConfigure,
        highlight,
    } = useContext(DragDropEditorContext);

    const { content } = useContext(LayerContext);

    const [ deleting, setDeleting ] = useState(false);
    const [ cursorBelow, setCursorBelow ] = useState<null | boolean>(null);

    const component = useDragDropComponent(id);

    const isSelected = id === selected;

    const item = useMemo(() => {
        if(!component) {
            return null;
        }

        const item = items[component.type];

        if(!item) {
            return null;
        }

        return item;
    }, [ component, items ]);

    // Keep reference updated
    indexRef.current = index;
    hasLayerRef.current = content.length !== 0;
    deletingRef.current = deleting;

    useEffect(() => {
        if(!isSelected) {
            deletingRef.current = false;
            setDeleting(false);

            return;
        }

        const listener = (e: KeyboardEvent) => {
            if(e.target instanceof HTMLInputElement) {
                return;
            }

            // Check to see if there is any focused element
            if(document.activeElement !== null && document.activeElement !== document.body) {
                return;
            }

            // Check to see if dialog is currently open
            if(store.getState().dialog.visible || hasLayerRef.current) {
                return;
            }

            if(e.key !== "Delete") {
                return;
            }

            if(deletingRef.current) {
                if(deletingTimeout.current) {
                    clearTimeout(deletingTimeout.current);
                }

                manager.deleteComponent(container, indexRef.current, items);
                setDeleting(false);

                return;
            }

            setDeleting(true);
            deletingTimeout.current = setTimeout(() => {
                setDeleting(false);
            }, 1000);
        };

        window.addEventListener("keydown", listener);

        return () => {
            if(deletingTimeout.current) {
                clearTimeout(deletingTimeout.current);
            }

            window.removeEventListener("keydown", listener);
        };
    }, [ isSelected, container, manager, items ]);

    const [{ isDragging }, drag, dragPreview ] = useDrag(() => ({
        type: component?.type ?? "",
        item: {
            type: component?.type,
            container,
            index,
        } as DragDropMoveItem,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [ component, container, index ]);

    const [{ isOver }, drop ] = useDrop(() => ({
        accept,
        collect: monitor => ({
            isOver: monitor.isOver({ shallow: true }),
        }),
        drop: (item: DragDropItem, monitor) => {
            if(monitor.isOver({ shallow: true })) {
                let detached: string | null;
                let newIndex = indexRef.current;

                if(isDragDropNew(item)) {
                    detached = manager.registerComponent(onCreate(item.type));
                } else {
                    detached = manager.detachComponent(item.container, item.index);

                    // Check if the item is being moved below itself in the same container
                    if(item.container === container && indexRef.current > item.index) {
                        // Fix the detaching of the dragging object changing the correct index to move to
                        newIndex--;
                    }
                }

                if(!detached) {
                    return;
                }

                if(belowRef.current) {
                    newIndex++;
                }

                manager.attachComponent(container, detached, newIndex);
            }
        },
        hover: (item, monitor) => {
            if(monitor.isOver({ shallow: true }) && containerRef.current) {
                const clientRect = containerRef.current.getBoundingClientRect();
                const dragOffset = monitor.getClientOffset();

                if(dragOffset) {
                    const localY = dragOffset.y - clientRect.top;

                    // Check if above or below
                    if(localY <= (clientRect.height / 2)) {
                        setCursorBelow(false);
                        belowRef.current = false;
                    } else {
                        setCursorBelow(true);
                        belowRef.current = true;
                    }
                }
            } else {
                setCursorBelow(null);
                belowRef.current = null;
            }
        },
    }), [ accept, onCreate ]);

    if(!component || !item) {
        return null;
    }

    const Component = typeof item.edit !== "undefined" ? item.edit : item.component;

    const errorNode = (
        <div
            className={
                "w-full h-full border-2 border-dashed border-gray-500 text-gray-500 " +
                "flex flex-col justify-center items-center p-4"
            }
        >
            <div className="text-4xl text-red-400">
                <FontAwesomeLight icon="exclamation-triangle" />
            </div>
            <div>{item.name} component in invalid state</div>
        </div>
    );

    const emptyNode = (
        <div
            className={
                "w-full h-full border-2 border-dashed border-gray-500 text-gray-500 " +
                "flex flex-col justify-center items-center p-4"
            }
        >
            <div className="text-4xl">
                <FontAwesomeLight icon={item.icon} />
            </div>
            <div>{item.name} component empty</div>
        </div>
    );

    return (
        <div
            ref={ref => {
                (containerRef as any).current = drop(ref);
            }}
            onClick={(e) => {
                e.stopPropagation();
                setSelected(id);
            }}
            className={
                "relative p-1 border border-dashed -mt-[1px]" +
                (isDragging ? " hidden" : "") +
                (isSelected ? " border-gray-800/80 cursor-auto z-10" : " border-grey-300 cursor-pointer") +
                (highlight === component.type ? " border-blue-400 bg-blue-50" : "")
            }
        >
            {isSelected && (
                <>
                    {deleting && (
                        <div
                            className={
                                "absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center " +
                                "text-3xl z-20"
                            }
                        >
                            <div
                                className={
                                    "flex justify-center items-center w-12 h-12 rounded-full backdrop-blur-xs " +
                                    "bg-neutral-800/70 text-red-300"
                                }
                            >
                                <FontAwesomeSolid icon="trash-alt" />
                            </div>
                        </div>
                    )}
                    <div
                        className={
                            "flex items-center absolute text-white bottom-full -left-[1px] -right-[1px] p-1 " +
                            "z-10 text-sm font-roboto " + (deleting ? "bg-red-800" : "bg-gray-800")
                        }
                    >
                        <div
                            ref={el => {
                                drag(el);
                            }}
                            className="cursor-grab"
                        >
                            <FontAwesomeSolid icon="arrows" />
                        </div>
                        <div className="grow text-center">
                            {item.name}
                        </div>
                        <div>
                            <button
                                type="button"
                                className={
                                    "bg-transparent hover:bg-white/50 transition-colors cursor-pointer p-1 rounded " +
                                    "text-white"
                                }
                                onClick={(e) => {
                                    e.stopPropagation();

                                    onConfigure();
                                }}
                            >
                                <FontAwesomeSolid icon="cog" />
                            </button>
                            <button
                                type="button"
                                className={
                                    "bg-transparent hover:bg-white/50 transition-colors cursor-pointer p-1 rounded " +
                                    "text-white"
                                }
                                onClick={(e) => {
                                    e.stopPropagation();

                                    setSelected(null);
                                    manager.deleteComponent(
                                        container,
                                        index,
                                        items
                                    );
                                }}
                            >
                                <FontAwesomeSolid icon="trash-alt" />
                            </button>
                            <button
                                type="button"
                                className={
                                    "bg-transparent hover:bg-white/50 transition-colors cursor-pointer p-1 rounded " +
                                    "text-white"
                                }
                                onClick={(e) => {
                                    e.stopPropagation();

                                    setSelected(null);
                                }}
                            >
                                <FontAwesomeSolid icon="times" />
                            </button>
                        </div>
                    </div>
                </>
            )}
            {isOver && (
                <div
                    className={
                        "absolute border-t-[4px] border-t-blue-400 left-0 right-0 z-10  " +
                        (cursorBelow ? "-bottom-[3px]" : "-top-[2px]")
                    }
                />
            )}
            <div
                ref={el => {
                    dragPreview(el);
                }}
            >
                <DragDropComponentErrorContext.Provider value={errorNode}>
                    <DragDropComponentEmptyContext.Provider value={emptyNode}>
                        <Component
                            value={component.value}
                            style={component.style}
                        />
                    </DragDropComponentEmptyContext.Provider>
                </DragDropComponentErrorContext.Provider>
            </div>
        </div>
    );
};
